import React, { useEffect, useState } from 'react';
import { FaSignOutAlt, FaChevronDown } from 'react-icons/fa';
// Deuxième import du même module que la ligne canAccessSettings/canImportPayroll
// ci-dessous : le placement fusionné conflicte avec un autre bolt en cours
// (plan bolt 034, § Fichiers à modifier) ; à fusionner à la consolidation
// post-merge, pas ici. `eslint-config-react-app` n'interdit pas ce doublon.
import { HiComputerDesktop, HiBookOpen } from 'react-icons/hi2';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SettingIcon } from '../Tools/IconSvg';
import { canAccessSettings } from '../Auth/permissions';
import { construireNavigation, estActive, lienDe } from '../Navigation/navigation';
import { useATraiter } from '../Navigation/ATraiter';

// Les sections que l'utilisateur a repliées, d'une visite à l'autre. Un
// stockage illisible ou indisponible laisse tout ouvert : c'est un confort.
const CLE_SECTIONS_REPLIEES = 'menuSectionsRepliees';

const lireSectionsRepliees = () => {
    try {
        const valeur = JSON.parse(localStorage.getItem(CLE_SECTIONS_REPLIEES) || '[]');
        return Array.isArray(valeur) ? valeur : [];
    } catch (e) {
        return [];
    }
};

const MenuNav = React.memo(({ isCollapsed, onLogoutRequest, user }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { compteurs } = useATraiter();
    const [repliees, setRepliees] = useState(lireSectionsRepliees);
    // Menu replié : la section survolée, et la hauteur où poser son volet.
    const [volet, setVolet] = useState(null);

    const handleLogoutRequest = () => {
        onLogoutRequest();
    };

    const activeClass =
        'bg-[--bg-color] text-[--color-primary] border-[--color-primary] [&_svg]:text-[--color-primary]';

    const inactiveClass = 'text-[--bg-color]';
    const hoverClass = 'hover:bg-[--bg-hover]  hover:border-[--color-primary]';
    const settingsAllowed = canAccessSettings(user);

    const activeCompany = user?.company_active;
    const hasDailySalaryInput = activeCompany?.has_daily_salary_input || false;

    // Sections métier et entrées : la carte de navigation (Yros/Kavero#416).
    // Le pied garde son rendu ci-dessous.
    const sections = construireNavigation(user, t).filter(({ id }) => id !== 'pied');
    const actif = (entree) => estActive(entree, location, user, t);
    const sectionCourante = sections.find(({ entrees }) => entrees.some(actif))?.id;

    // La section de la page courante s'ouvre toute seule, qu'on arrive par un
    // lien, la recherche ou le retour du navigateur. Elle reste repliable une
    // fois sur la page : seule l'arrivée la rouvre.
    useEffect(() => {
        if (!sectionCourante) return;
        setRepliees((courantes) =>
            courantes.includes(sectionCourante) ? courantes.filter((id) => id !== sectionCourante) : courantes
        );
    }, [sectionCourante, location.pathname, location.search]);

    useEffect(() => {
        try {
            localStorage.setItem(CLE_SECTIONS_REPLIEES, JSON.stringify(repliees));
        } catch (e) {
            // Stockage indisponible : le repli vaut pour la visite.
        }
    }, [repliees]);

    useEffect(() => {
        setVolet(null);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!volet) return undefined;
        const fermerSurEchap = (evenement) => {
            if (evenement.key === 'Escape') setVolet(null);
        };
        document.addEventListener('keydown', fermerSurEchap);
        return () => document.removeEventListener('keydown', fermerSurEchap);
    }, [volet]);

    const basculer = (id) =>
        setRepliees((courantes) =>
            courantes.includes(id) ? courantes.filter((autre) => autre !== id) : [...courantes, id]
        );

    const ouvrirVolet = (id, element) => setVolet({ id, haut: element.getBoundingClientRect().top });
    const fermerVolet = () => setVolet(null);

    // Menu replié, les icônes restent les liens : un toucher mène droit à la
    // page, et le volet n'est qu'une aide visuelle.
    const survolVolet = (section) => ({
        onMouseEnter: (evenement) => ouvrirVolet(section.id, evenement.currentTarget),
        onMouseLeave: fermerVolet,
        onFocus: (evenement) => ouvrirVolet(section.id, evenement.currentTarget),
        onBlur: (evenement) => {
            if (!evenement.currentTarget.contains(evenement.relatedTarget)) fermerVolet();
        },
    });

    // `enfant` : une entrée sous un grand menu, en retrait et un cran plus
    // petite que lui — la hiérarchie se lit à la taille, pas qu'à la couleur.
    const rendreEntree = (entree, enfant = false) => {
        const estActif = actif(entree);
        const { Icone } = entree;
        // La pastille « À traiter » : son nombre se lit dans le nom du lien,
        // pas dans un chiffre isolé.
        const enAttente = entree.pastille && compteurs ? compteurs[entree.pastille] || 0 : 0;
        return (
            <li key={entree.id}>
                <Link
                    to={lienDe(entree)}
                    aria-current={estActif ? 'page' : undefined}
                    aria-label={enAttente > 0 ? `${entree.libelle}, ${t('aTraiter.enAttente', { count: enAttente })}` : undefined}
                    title={isCollapsed ? entree.libelle : ''}
                    className={`flex w-full py-2 ${estActif ? activeClass : `${inactiveClass} ${hoverClass}`} ${isCollapsed ? 'justify-center relative' : ''}`}
                >
                    {estActif && !isCollapsed && (
                        <div className='border-[3px] h-[30px] justify-start rounded-lg border-orange-400'></div>
                    )}
                    {estActif && isCollapsed && (
                        <div className='absolute left-0 top-1/2 -translate-y-1/2 border-[3px] h-[30px] rounded-lg border-orange-400'></div>
                    )}
                    <div className={`flex items-center gap-2 ${isCollapsed ? 'pl-0 justify-center' : enfant ? 'pl-3 text-sm font-medium' : 'pl-4'}`}>
                        <Icone className={enfant ? 'w-5 h-5' : 'w-6 h-6'} /> {!isCollapsed && entree.libelle}
                    </div>
                    {enAttente > 0 && (
                        <span
                            aria-hidden="true"
                            className={`rounded-full bg-orange-500 text-center font-bold text-white ${
                                isCollapsed
                                    ? 'absolute right-2 top-0 min-w-[16px] px-1 text-[10px] leading-4'
                                    : 'ml-auto mr-4 self-center min-w-[20px] px-1.5 text-[11px] leading-5'
                            }`}
                        >
                            {enAttente > 99 ? '99+' : enAttente}
                        </span>
                    )}
                </Link>
            </li>
        );
    };

    return (
        <div className="flex flex-col font-semibold justify-between h-full text-white py-4">
            <div className={isCollapsed ? 'space-y-5' : 'space-y-2'}>
                {sections.map((section) => {
                    const ouverte = isCollapsed || !section.titre || !repliees.includes(section.id);
                    const enRetrait = Boolean(section.titre) && !isCollapsed;
                    const { Icone: IconeSection } = section;
                    return (
                        <div
                            key={section.id}
                            {...(section.titre
                                ? { role: 'group', 'aria-label': section.titre }
                                : { 'data-testid': 'menu-accueil' })}
                            {...(isCollapsed && section.titre ? survolVolet(section) : {})}
                        >
                            {section.titre && (isCollapsed ? (
                                // Menu replié : un filet sépare les sections, le
                                // nom du groupe reste lu par les lecteurs d'écran.
                                <div aria-hidden="true" className="mx-3 mb-3 border-t border-[--bg-color] opacity-30" />
                            ) : (
                                // Un grand menu est une ligne de premier niveau,
                                // comme « Tableau de bord » : icône, libellé,
                                // chevron. La section de la page courante se
                                // signale par un fond, même repliée.
                                <button
                                    type="button"
                                    onClick={() => basculer(section.id)}
                                    aria-expanded={ouverte}
                                    aria-controls={`menu-section-${section.id}`}
                                    data-courante={section.id === sectionCourante}
                                    className={`flex w-full items-center gap-2 py-2 pl-4 pr-4 text-left text-[--bg-color] transition-colors hover:bg-[--bg-hover] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[--bg-color] ${
                                        section.id === sectionCourante ? 'bg-white/10' : ''
                                    }`}
                                >
                                    {IconeSection && <IconeSection aria-hidden="true" className="w-6 h-6 shrink-0" />}
                                    <span className="flex-1">{section.titre}</span>
                                    <FaChevronDown
                                        aria-hidden="true"
                                        className={`w-3 h-3 shrink-0 opacity-80 transition-transform duration-200 ${ouverte ? '' : '-rotate-90'}`}
                                    />
                                </button>
                            ))}
                            <ul
                                id={`menu-section-${section.id}`}
                                hidden={!ouverte}
                                data-niveau={enRetrait ? '2' : '1'}
                                className={enRetrait ? 'mt-1 ml-7 space-y-1 border-l border-white/25' : 'space-y-2'}
                            >
                                {section.entrees.map((entree) => rendreEntree(entree, enRetrait))}
                            </ul>
                            {isCollapsed && volet?.id === section.id && (
                                // `fixed` échappe au débordement caché de la barre
                                // latérale, comme la liste des sociétés.
                                <div
                                    aria-hidden="true"
                                    data-testid={`volet-${section.id}`}
                                    className="fixed left-[70px] z-50 w-60 overflow-y-auto rounded-r-lg bg-[--color-primary] py-3 text-left shadow-lg"
                                    style={{ top: volet.haut, maxHeight: `calc(100vh - ${volet.haut}px - 8px)` }}
                                >
                                    <p className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-[--bg-color] opacity-75">
                                        {section.titre}
                                    </p>
                                    <ul className="space-y-1">
                                        {section.entrees.map((entree) => (
                                            <li key={entree.id}>
                                                <Link
                                                    to={lienDe(entree)}
                                                    tabIndex={-1}
                                                    className={`block px-4 py-2 ${actif(entree) ? activeClass : `${inactiveClass} ${hoverClass}`}`}
                                                >
                                                    {entree.libelle}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className={`mt-6 border-t pt-4 ${isCollapsed ? 'text-center' : ''}`}>
                <ul className="space-y-2">
                    <li>
                        <a
                            href="https://yros.io/simulateur/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 py-2 ${isCollapsed ? 'justify-center' : 'pl-5'} rounded-md text-white`}
                            title={isCollapsed ? t('sidebar.onlineSimulator') : t('sidebar.onlineSimulatorNewTab')}
                            aria-label={t('sidebar.onlineSimulatorAriaLabel')}
                        >
                            <HiComputerDesktop  className="w-6 h-6" />
                            {!isCollapsed && (
                                <span className="flex items-center gap-1">
                                    {t('sidebar.onlineSimulator')}
                                </span>
                            )}
                        </a>
                    </li>
                    <li>
                        <NavLink
                            to="/guide"
                            className={({ isActive }) => 
                                `flex items-center gap-2 py-2 ${isCollapsed ? 'justify-center relative' : 'pl-5'} rounded-md text-white`
                            }
                            aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
                            title={isCollapsed ? t('sidebar.userGuide') : ""}
                        >
                            {({ isActive }) => (
                                <>
                                    {isActive && isCollapsed && (
                                        <div className='absolute left-0 top-1/2 -translate-y-1/2 border-[3px] h-[30px] rounded-lg border-orange-400'></div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <HiBookOpen className="w-6 h-6" />
                                        {!isCollapsed && t('sidebar.userGuide')}
                                    </div>
                                </>
                            )}
                        </NavLink>
                    </li>
                    {settingsAllowed && (
                        <li>
                            <NavLink
                                to="/settings"
                                className={({ isActive }) => 
                                    `flex items-center gap-2 py-2 ${isCollapsed ? 'justify-center relative' : 'pl-5'} rounded-md text-white`
                                }
                                aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
                                title={isCollapsed ? t('sidebar.settings') : ""}
                            >
                                {({ isActive }) => (
                                    <>
                                        {isActive && isCollapsed && (
                                            <div className='absolute left-0 top-1/2 -translate-y-1/2 border-[3px] h-[30px] rounded-lg border-orange-400'></div>
                                        )}
                                        <div className="flex items-center gap-2">
                                            <SettingIcon className="w-6 h-6" />
                                            {!isCollapsed && t('sidebar.settings')}
                                        </div>
                                    </>
                                )}
                            </NavLink>
                        </li>
                    )}
                    <li>
                        <button
                            onClick={handleLogoutRequest}
                            className={`flex items-center gap-2 py-2 ${isCollapsed ? 'justify-center w-full' : 'pl-6'} rounded-md text-white`}
                            title={isCollapsed ? t('sidebar.logout') : ""}
                        >
                            <FaSignOutAlt className="text-white" /> {!isCollapsed && t('sidebar.logout')}
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    );
});

export default MenuNav;
